/**
 * Combined Summary Card
 * Sums verified provider spend only when every amount uses the same currency.
 */

import {
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Minus,
  DollarSign,
  Cloud,
  Phone,
  Server,
  Brain,
  AudioLines,
} from 'lucide-react';
import type { SpendSummary, Provider } from '../types';

interface ProviderSummary {
  provider: Provider;
  summary: SpendSummary;
  currency: string;
}

interface CombinedSummaryCardProps {
  providers: ProviderSummary[];
}

const providerConfig: Record<
  Provider,
  {
    label: string;
    icon: typeof Cloud;
    barClassName: string;
    iconClassName: string;
  }
> = {
  gcp: {
    label: 'GCP',
    icon: Cloud,
    barClassName: 'bg-blue-500',
    iconClassName: 'text-blue-500',
  },
  twilio: {
    label: 'Twilio',
    icon: Phone,
    barClassName: 'bg-green-500',
    iconClassName: 'text-green-500',
  },
  aws: {
    label: 'AWS',
    icon: Server,
    barClassName: 'bg-orange-500',
    iconClassName: 'text-orange-500',
  },
  openai: {
    label: 'OpenAI',
    icon: Brain,
    barClassName: 'bg-purple-500',
    iconClassName: 'text-purple-500',
  },
  deepgram: {
    label: 'Deepgram',
    icon: AudioLines,
    barClassName: 'bg-cyan-500',
    iconClassName: 'text-cyan-500',
  },
};

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export function CombinedSummaryCard({ providers }: CombinedSummaryCardProps) {
  const currencies = [...new Set(providers.map((provider) => provider.currency))];
  if (currencies.length !== 1) {
    return (
      <div
        role="alert"
        className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-6 text-red-900"
      >
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        <div>
          <div className="font-semibold">Combined total unavailable</div>
          <div className="mt-1 text-sm text-red-700">
            Verified providers returned different currencies. Review each provider separately;
            no cross-currency total was calculated.
          </div>
        </div>
      </div>
    );
  }

  const currency = currencies[0];
  const combinedCurrentMonth = providers.reduce(
    (sum, provider) => sum + provider.summary.currentMonth.total,
    0,
  );
  const combinedPreviousMonth = providers.reduce(
    (sum, provider) => sum + provider.summary.previousMonth.total,
    0,
  );
  const combinedProjected = providers.reduce(
    (sum, provider) => sum + provider.summary.projectedTotal,
    0,
  );
  const combinedPercentChange = combinedPreviousMonth > 0
    ? ((combinedCurrentMonth - combinedPreviousMonth) / combinedPreviousMonth) * 100
    : 0;
  const isIncrease = combinedPercentChange > 0;
  const isDecrease = combinedPercentChange < 0;
  const dateRange = providers[0]?.summary.currentMonth;
  const includedProviders = providers
    .map((provider) => providerConfig[provider.provider].label)
    .join(' + ');
  const providerBreakdown = providers.map((provider) => {
    const current = provider.summary.currentMonth.total;
    return {
      ...provider,
      current,
      percent: combinedCurrentMonth > 0
        ? Math.round((current / combinedCurrentMonth) * 100)
        : 0,
      config: providerConfig[provider.provider],
    };
  });

  return (
    <div className="rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 p-6">
      <div className="mb-4 flex items-center gap-2 text-sm font-medium text-blue-700">
        <DollarSign className="h-5 w-5" />
        <span>Verified Cloud Spend ({includedProviders})</span>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div>
          <div className="mb-1 text-sm text-gray-500">Current Month Total</div>
          <div className="text-3xl font-bold text-gray-900">
            {formatCurrency(combinedCurrentMonth, currency)}
          </div>
          {dateRange && (
            <div className="mt-1 text-sm text-gray-500">
              {formatDate(dateRange.startDate)} - {formatDate(dateRange.endDate)}
            </div>
          )}
          <div className="mt-2 flex items-center gap-1">
            {isIncrease && <ArrowUp className="h-4 w-4 text-red-500" />}
            {isDecrease && <ArrowDown className="h-4 w-4 text-green-500" />}
            {!isIncrease && !isDecrease && <Minus className="h-4 w-4 text-gray-400" />}
            <span
              className={`text-sm font-medium ${
                isIncrease ? 'text-red-600' : isDecrease ? 'text-green-600' : 'text-gray-500'
              }`}
            >
              {Math.abs(combinedPercentChange).toFixed(1)}% vs last month
            </span>
          </div>
        </div>

        <div>
          <div className="mb-1 text-sm text-gray-500">Previous Month Total</div>
          <div className="text-3xl font-bold text-gray-900">
            {formatCurrency(combinedPreviousMonth, currency)}
          </div>
        </div>

        <div>
          <div className="mb-1 text-sm text-gray-500">Projected Month-End</div>
          <div className="text-3xl font-bold text-blue-600">
            {formatCurrency(combinedProjected, currency)}
          </div>
          <div className="mt-1 text-sm text-gray-500">Based on current usage rate</div>
        </div>
      </div>

      <div className="mt-6 border-t border-blue-200 pt-4">
        <div className="mb-2 text-sm text-gray-600">Provider Breakdown</div>
        <div className="flex h-3 overflow-hidden rounded-full bg-gray-200">
          {providerBreakdown.map(({ provider, current, percent, config }) => (
            <div
              key={provider}
              className={`${config.barClassName} transition-all duration-300`}
              style={{ width: `${percent}%` }}
              title={`${config.label}: ${formatCurrency(current, currency)} (${percent}%)`}
            />
          ))}
        </div>
        <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-gray-600">
          {providerBreakdown.map(({ provider, current, percent, config }) => {
            const Icon = config.icon;
            return (
              <div key={provider} className="flex items-center gap-1">
                <Icon className={`h-3 w-3 ${config.iconClassName}`} />
                <span>{config.label}: {formatCurrency(current, currency)}</span>
                <span className="text-gray-400">({percent}%)</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
